(() => {
  document.documentElement.classList.add('bh-js');

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function initReveals(scope) {
    const elements = scope.querySelectorAll('[data-bh-reveal]:not([data-bh-reveal-ready])');

    if (!elements.length) return;

    if (prefersReducedMotion.matches || !('IntersectionObserver' in window)) {
      elements.forEach((element) => {
        element.dataset.bhRevealReady = 'true';
        element.classList.add('is-visible');
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.08 }
    );

    elements.forEach((element) => {
      element.dataset.bhRevealReady = 'true';
      observer.observe(element);
    });
  }

  function loadVideo(video) {
    if (video.dataset.bhLoaded === 'true') return;

    const source = video.dataset.src;
    if (!source) return;

    video.src = source;
    video.dataset.bhLoaded = 'true';
    video.load();
  }

  function setVideoState(video, isPlaying) {
    const card = video.closest('.bh-motion-card');
    const button = card?.querySelector('[data-bh-video-toggle]');
    const label = button?.querySelector('[data-bh-video-label]');

    card?.classList.toggle('is-playing', isPlaying);
    if (!button || !label) return;

    const currentLabel = button.getAttribute('aria-label') || 'video';
    const subject = button.dataset.bhVideoSubject || currentLabel.replace(/^(Reproducir|Pausar)\s+/i, '');
    button.dataset.bhVideoSubject = subject;
    button.setAttribute('aria-label', `${isPlaying ? 'Pausar' : 'Reproducir'} ${subject}`);
    label.textContent = isPlaying ? 'Pausar' : 'Reproducir';
  }

  function pauseOtherVideos(activeVideo) {
    document.querySelectorAll('.bh-motion-card video').forEach((video) => {
      if (video !== activeVideo && !video.paused) video.pause();
    });
  }

  function initMotion(scope) {
    const sections = scope.querySelectorAll('[data-bh-motion]:not([data-bh-motion-ready])');

    sections.forEach((section) => {
      section.dataset.bhMotionReady = 'true';
      const videos = [...section.querySelectorAll('video[data-src]')];

      videos.forEach((video) => {
        const button = video.closest('.bh-motion-card')?.querySelector('[data-bh-video-toggle]');

        video.addEventListener('play', () => {
          pauseOtherVideos(video);
          setVideoState(video, true);
        });
        video.addEventListener('pause', () => setVideoState(video, false));

        button?.addEventListener('click', () => {
          loadVideo(video);

          if (video.paused) {
            video.play().catch(() => setVideoState(video, false));
          } else {
            video.pause();
          }
        });
      });

      if (!('IntersectionObserver' in window)) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const sectionVideos = entry.target.querySelectorAll('video[data-src]');

            if (!entry.isIntersecting) sectionVideos.forEach((video) => video.pause());
          });
        },
        { threshold: 0.05 }
      );

      observer.observe(section);
    });
  }

  function initSkipToContent(scope) {
    const main = document.getElementById('MainContent');
    if (main && !main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');

    scope.querySelectorAll('a[href="#MainContent"]:not([data-bh-skip-ready])').forEach((link) => {
      link.dataset.bhSkipReady = 'true';
      link.addEventListener('click', () => {
        window.requestAnimationFrame(() => main?.focus({ preventScroll: true }));
      });
    });
  }

  function initCategoryNavigation(scope) {
    const selector = '.bh-category-nav__inner:not([data-bh-nav-ready])';
    const navigationRows = new Set(scope.querySelectorAll?.(selector) || []);
    if (scope instanceof Element && scope.matches(selector)) navigationRows.add(scope);

    navigationRows.forEach((row) => {
      row.dataset.bhNavReady = 'true';
      const activeLink = row.querySelector('.is-active');
      if (!activeLink || !window.matchMedia('(max-width: 749px)').matches) return;

      window.requestAnimationFrame(() => {
        row.scrollLeft = Math.max(0, activeLink.offsetLeft - (row.clientWidth - activeLink.offsetWidth) / 2);
      });
    });
  }

  function initContactForms(scope) {
    scope.querySelectorAll('.contact-form__form').forEach((form) => {
      ['contact[name]', 'contact[email]', 'contact[body]'].forEach((name) => {
        const field = form.elements.namedItem(name);
        if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) return;
        field.required = true;
        field.setAttribute('aria-required', 'true');
      });
    });

    scope.querySelectorAll('.bh-complaints__success').forEach((message) => {
      if (!(message instanceof HTMLElement)) return;
      const parentForm = message.closest('form');
      if (window.location.hash && parentForm?.id && window.location.hash !== `#${parentForm.id}`) {
        message.hidden = true;
        return;
      }
      if (message.dataset.bhFocused === 'true') return;
      message.dataset.bhFocused = 'true';
      message.focus({ preventScroll: true });
    });
  }

  function limaNowParts() {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Lima',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date());
    const values = {};

    parts.forEach((part) => {
      if (part.type !== 'literal') values[part.type] = Number(part.value);
    });

    return values;
  }

  function utcDateFromParts(parts) {
    return new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
  }

  function addUtcDays(date, days) {
    const result = new Date(date.getTime());
    result.setUTCDate(result.getUTCDate() + days);
    return result;
  }

  function nextBlessHomeBusinessDay(date) {
    let result = addUtcDays(date, 1);
    while (result.getUTCDay() === 0) result = addUtcDays(result, 1);
    return result;
  }

  function earliestProductDeliveryDate() {
    const now = limaNowParts();
    const today = utcDateFromParts(now);
    const isSunday = today.getUTCDay() === 0;
    const isPastCutoff = now.hour >= 18;

    if (isSunday || isPastCutoff) {
      return nextBlessHomeBusinessDay(nextBlessHomeBusinessDay(today));
    }

    return nextBlessHomeBusinessDay(today);
  }

  function initProductDeliveryEstimates(scope) {
    const selector = '[data-bh-estimated-delivery]:not([data-bh-estimate-ready])';
    const estimates = new Set(scope.querySelectorAll?.(selector) || []);

    if (scope instanceof Element && scope.matches(selector)) estimates.add(scope);
    if (!estimates.size) return;

    const date = earliestProductDeliveryDate();
    const isoDate = [
      date.getUTCFullYear(),
      String(date.getUTCMonth() + 1).padStart(2, '0'),
      String(date.getUTCDate()).padStart(2, '0'),
    ].join('-');
    const dateLabel = new Intl.DateTimeFormat('es-PE', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      timeZone: 'UTC',
    }).format(date);

    estimates.forEach((estimate) => {
      estimate.dataset.bhEstimateReady = 'true';
      estimate.dateTime = isoDate;
      estimate.textContent = `Desde el ${dateLabel} tras validar el pago, salvo feriados`;
    });
  }

  let variantStateReady = false;

  function initProductVariantState() {
    if (variantStateReady) return;
    variantStateReady = true;

    document.addEventListener('bh:variant-change', (event) => {
      const variant = event.detail || {};

      document.querySelectorAll('[data-bh-product-information]').forEach((section) => {
        section.dataset.bhProductAvailable = String(variant.available !== false);
      });
      document.querySelectorAll('[data-bh-product-availability]').forEach((status) => {
        status.textContent = variant.available === false ? 'Agotado' : 'Disponible';
      });
      document.querySelectorAll('[data-bh-product-sku]').forEach((sku) => {
        sku.textContent = variant.sku || '';
        const row = sku.closest('[data-bh-product-sku-row]');
        if (row) row.hidden = !variant.sku;
      });
      document.querySelectorAll('[data-bh-estimated-delivery]').forEach((estimate) => {
        if (variant.available === false) {
          estimate.dataset.bhAvailableText = estimate.textContent;
          estimate.dataset.bhAvailableDate = estimate.dateTime || '';
          estimate.textContent = 'Sin fecha disponible';
          estimate.removeAttribute('datetime');
        } else if (estimate.dataset.bhAvailableText) {
          estimate.textContent = estimate.dataset.bhAvailableText;
          if (estimate.dataset.bhAvailableDate) estimate.dateTime = estimate.dataset.bhAvailableDate;
        }
      });

    });
  }

  function initCheckoutDetails(scope, force = false) {
    const selector = '[data-bh-checkout-details]';
    const sections = new Set(scope.querySelectorAll?.(selector) || []);

    if (scope instanceof Element) {
      if (scope.matches(selector)) sections.add(scope);
      const parentSection = scope.closest(selector);
      if (parentSection) sections.add(parentSection);
    }

    sections.forEach((section) => {
      if (!force && section.dataset.bhCheckoutReady === 'true') return;

      if (typeof section.bhCheckoutCleanup === 'function') section.bhCheckoutCleanup();

      section.dataset.bhCheckoutReady = 'true';

      const form = section.closest('form');
      if (typeof form?.bhCheckoutCleanup === 'function') form.bhCheckoutCleanup();
      const checkoutScope = form?.closest('cart-items-component') || form?.parentElement || document;
      const destination = section.querySelector('[data-bh-destination]');
      const localFields = section.querySelector('[data-bh-local-delivery]');
      const provinceFields = section.querySelector('[data-bh-province-delivery]');
      const deliverySummary = section.querySelector('[data-bh-delivery-summary]');
      const deliveryDate = section.querySelector('[data-bh-delivery-date]');
      const deliveryWindow = section.querySelector('[data-bh-delivery-window]');
      const afternoonOption = section.querySelector('[data-bh-afternoon]');
      const agency = section.querySelector('[data-bh-agency]');
      const shippingRate = section.querySelector('[data-bh-shipping-rate]');
      const shippingStatus = section.querySelector('[data-bh-shipping-status]');
      const shippingLinesData = section.querySelector('[data-bh-shipping-lines]');
      const receipt = section.querySelector('[data-bh-receipt]');
      const boletaFields = section.querySelector('[data-bh-boleta]');
      const facturaFields = section.querySelector('[data-bh-factura]');
      const dni = section.querySelector('[data-bh-dni]');
      const ruc = section.querySelector('[data-bh-ruc]');
      const businessName = section.querySelector('[data-bh-business-name]');
      const fiscalAddress = section.querySelector('[data-bh-fiscal-address]');
      const installation = section.querySelector('[data-bh-installation]');
      const installationStatus = section.querySelector('[data-bh-installation-status]');
      const attributesData = section.querySelector('[data-bh-cart-attributes]');
      const checkoutButtons = [...checkoutScope.querySelectorAll('[name="checkout"]')].filter(
        (button) => button.form === form || button.getAttribute('form') === form?.id
      );
      const cartTotalItem = checkoutScope.querySelector('.cart-totals__total, .cart__total');
      const cartTotalLabel = cartTotalItem?.querySelector('.cart-totals__total-label, .cart__total-label');
      const cartTotalValue = cartTotalItem?.querySelector(
        '.cart-totals__total-value, .cart__total-value, [data-testid="cart-total-value"]'
      );
      const originalCartTotalLabel = cartTotalLabel?.textContent || 'Total estimado';
      let originalCartTotalValue = section.dataset.bhCartTotalFormatted || cartTotalValue?.textContent;
      let isFreeShipping = section.dataset.bhFreeShipping === 'true';
      let hasMerchandise = section.dataset.bhHasMerchandise !== 'false';
      const shippingConfigured = section.dataset.bhShippingConfigured === 'true';
      const shippingVariantIds = new Set(
        ['30', '40', '50']
          .map((price) => Number(section.dataset[`bhZone${price}VariantId`]))
          .filter(Boolean)
      );
      const freeShippingThreshold = Number(section.dataset.bhFreeShippingThreshold) || 150000;
      const shippingHandle = 'tarifa-de-envio';
      const installationHandle = 'instalacion-de-mesa';
      const controller = new AbortController();
      const { signal } = controller;
      let saveTimer;
      let cartStateRefreshTimer;
      let saveChain = Promise.resolve();
      let storedAttributes = {};
      let shippingLines = [];
      let checkoutInProgress = false;
      let cartMutationPending = false;
      let cartMutationVersion = 0;
      let cartStateVersion = 0;

      const cleanup = () => {
        window.clearTimeout(saveTimer);
        window.clearTimeout(cartStateRefreshTimer);
        controller.abort();
        if (form?.bhCheckoutCleanup === cleanup) delete form.bhCheckoutCleanup;
      };
      section.bhCheckoutCleanup = cleanup;
      if (form) form.bhCheckoutCleanup = cleanup;

      try {
        storedAttributes = JSON.parse(attributesData?.textContent || '{}');
      } catch (_error) {
        storedAttributes = {};
      }

      try {
        shippingLines = JSON.parse(shippingLinesData?.textContent || '[]');
      } catch (_error) {
        shippingLines = [];
      }

      function limaDateParts() {
        const parts = new Intl.DateTimeFormat('en-CA', {
          timeZone: 'America/Lima',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hourCycle: 'h23',
        }).formatToParts(new Date());
        const values = {};

        parts.forEach((part) => {
          if (part.type !== 'literal') values[part.type] = Number(part.value);
        });

        return values;
      }

      function utcDate(parts) {
        return new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
      }

      function addDays(date, days) {
        const result = new Date(date.getTime());
        result.setUTCDate(result.getUTCDate() + days);
        return result;
      }

      function nextBusinessDay(date) {
        let result = addDays(date, 1);
        while (result.getUTCDay() === 0) result = addDays(result, 1);
        return result;
      }

      function formatDate(date) {
        return [
          date.getUTCFullYear(),
          String(date.getUTCMonth() + 1).padStart(2, '0'),
          String(date.getUTCDate()).padStart(2, '0'),
        ].join('-');
      }

      function earliestDeliveryDate() {
        const now = limaDateParts();
        const today = utcDate(now);
        const isSunday = today.getUTCDay() === 0;
        const isPastCutoff = now.hour >= 18;

        if (isSunday || isPastCutoff) {
          const validationDay = nextBusinessDay(today);
          return nextBusinessDay(validationDay);
        }

        return nextBusinessDay(today);
      }

      function updateDateRules() {
        if (!deliveryDate) return;

        const minimum = formatDate(earliestDeliveryDate());
        deliveryDate.min = minimum;

        if (deliveryDate.value && deliveryDate.value < minimum) deliveryDate.value = '';

        const chosenDate = deliveryDate.value
          ? new Date(`${deliveryDate.value}T00:00:00Z`)
          : null;
        const isSunday = chosenDate?.getUTCDay() === 0;
        const isSaturday = chosenDate?.getUTCDay() === 6;

        deliveryDate.setCustomValidity(isSunday ? 'Selecciona un día hábil de lunes a sábado.' : '');

        if (afternoonOption) afternoonOption.disabled = Boolean(isSaturday);
        if (isSaturday && deliveryWindow?.selectedOptions[0] === afternoonOption) {
          deliveryWindow.value = '';
        }
      }

      function setGroup(group, visible) {
        if (!group) return;
        group.hidden = !visible;
      }

      function setRequired(field, required) {
        if (!field) return;
        field.required = required;
        field.setAttribute('aria-required', String(required));
      }

      function setCheckoutBlocked(blocked) {
        checkoutButtons.forEach((button) => {
          button.disabled = blocked;
          button.toggleAttribute('aria-disabled', blocked);
        });
      }

      function clearField(field) {
        if (field) field.value = '';
      }

      function selectedDeliveryKind() {
        return destination?.selectedOptions[0]?.dataset.deliveryKind || '';
      }

      function selectedShippingVariantId() {
        const option = destination?.selectedOptions[0];
        if (!hasMerchandise || option?.dataset.deliveryKind !== 'local' || isFreeShipping) return 0;
        return Number(section.dataset[`bhZone${option.dataset.zonePrice}VariantId`]) || 0;
      }

      function setShippingStatus(message = '', isError = false) {
        if (shippingStatus) {
          shippingStatus.textContent = message;
          shippingStatus.classList.toggle('is-error', Boolean(message && isError));
        }
        destination?.setCustomValidity(isError ? message : '');
      }

      function createCartEstimateRow(attribute, label) {
        const row = document.createElement('span');
        const rowLabel = document.createElement('span');
        const rowValue = document.createElement('strong');

        row.className = 'bh-cart-estimate__row';
        row.setAttribute(attribute, '');
        row.hidden = true;
        rowLabel.className = 'bh-cart-estimate__label cart-primary-typography';
        rowLabel.textContent = label;
        rowValue.className = 'bh-cart-estimate__value cart-secondary-typography';
        row.append(rowLabel, rowValue);

        return row;
      }

      function ensureCartEstimateRows() {
        if (!cartTotalItem?.parentElement) return {};

        let shippingRow = checkoutScope.querySelector('[data-bh-cart-shipping-row]');
        let estimatedTotalRow = checkoutScope.querySelector('[data-bh-cart-estimated-total-row]');

        if (!shippingRow) {
          shippingRow = createCartEstimateRow('data-bh-cart-shipping-row', 'Envío');
          cartTotalItem.insertAdjacentElement('afterend', shippingRow);
        }
        if (!estimatedTotalRow) {
          estimatedTotalRow = createCartEstimateRow('data-bh-cart-estimated-total-row', 'Total estimado con envío');
          shippingRow.insertAdjacentElement('afterend', estimatedTotalRow);
        }

        return { shippingRow, estimatedTotalRow };
      }

      function updateCartEstimate(kind, option) {
        const { shippingRow, estimatedTotalRow } = ensureCartEstimateRows();
        if (!shippingRow || !estimatedTotalRow) return;

        const shippingLabel = shippingRow.querySelector('.bh-cart-estimate__label');
        const shippingValue = shippingRow.querySelector('.bh-cart-estimate__value');
        const estimatedLabel = estimatedTotalRow.querySelector('.bh-cart-estimate__label');
        const estimatedValue = estimatedTotalRow.querySelector('.bh-cart-estimate__value');

        if (!kind) {
          shippingRow.hidden = true;
          estimatedTotalRow.hidden = true;
          if (cartTotalLabel) cartTotalLabel.textContent = originalCartTotalLabel;
          if (cartTotalValue) cartTotalValue.textContent = originalCartTotalValue;
          return;
        }

        shippingRow.hidden = false;
        estimatedTotalRow.hidden = false;
        if (cartTotalLabel) cartTotalLabel.textContent = 'Subtotal antes de envío';
        if (cartTotalValue) cartTotalValue.textContent = section.dataset.bhCartTotalFormatted;

        if (kind === 'local') {
          const zonePrice = option?.dataset.zonePrice || '';
          const zoneLabel = option?.dataset.zoneLabel || 'Distrito tarifado';
          const shippingFormatted = section.dataset[`bhZone${zonePrice}ShippingFormatted`] || `S/${zonePrice}`;
          const totalFormatted = section.dataset[`bhZone${zonePrice}TotalFormatted`] || section.dataset.bhCartTotalFormatted;

          if (shippingLabel) shippingLabel.textContent = `Envío — ${zoneLabel}`;
          if (shippingValue) {
            shippingValue.textContent = isFreeShipping
              ? 'Gratis'
              : shippingFormatted;
          }
          if (estimatedLabel) estimatedLabel.textContent = 'Total del pedido';
          if (estimatedValue) {
            estimatedValue.textContent = isFreeShipping
              ? section.dataset.bhCartTotalFormatted
              : totalFormatted;
          }
          return;
        }

        if (shippingLabel) shippingLabel.textContent = 'Envío por agencia';
        if (shippingValue) shippingValue.textContent = 'Flete por pagar';
        if (estimatedLabel) estimatedLabel.textContent = 'Total pagado en Shopify';
        if (estimatedValue) estimatedValue.textContent = section.dataset.bhCartTotalFormatted;
      }

      function updateDeliveryState() {
        const option = destination?.selectedOptions[0];
        const kind = option?.dataset.deliveryKind || '';
        const isLocal = kind === 'local';
        const isProvince = kind === 'province';
        const zoneLabel = option?.dataset.zoneLabel || '';
        const zonePrice = option?.dataset.zonePrice || '';

        setShippingStatus();

        setGroup(localFields, isLocal);
        setGroup(provinceFields, isProvince);
        setRequired(deliveryDate, isLocal);
        setRequired(deliveryWindow, isLocal);
        setRequired(agency, isProvince);

        if (!isLocal) {
          clearField(deliveryDate);
          clearField(deliveryWindow);
        }
        if (!isProvince) clearField(agency);

        if (shippingRate) {
          if (isLocal && isFreeShipping) {
            shippingRate.value = `${zoneLabel} — Envío gratis desde S/1,500`;
          } else if (isLocal) {
            shippingRate.value = `${zoneLabel} — S/${zonePrice}`;
          } else if (isProvince) {
            shippingRate.value = 'Provincia — Flete por pagar en destino';
          } else {
            shippingRate.value = '';
          }
        }

        updateDateRules();
        updateCartEstimate(kind, option);

        if (!deliverySummary) return;

        if (isLocal && isFreeShipping) {
          deliverySummary.textContent = `Tu pedido califica para envío gratis en ${zoneLabel}. El beneficio se calculó sobre el total del carrito después de descuentos.`;
        } else if (isLocal) {
          const remaining = section.dataset.bhFreeShippingRemainingFormatted;
          deliverySummary.textContent = `${zoneLabel}: S/${zonePrice}, una sola vez por pedido. El total mostrado incluye esta tarifa.${remaining ? ` Te faltan ${remaining} para obtener envío gratis.` : ''}`;
        } else if (isProvince) {
          deliverySummary.textContent = 'Envío por la agencia elegida. El flete se paga en destino, directamente a la agencia; no forma parte del total pagado en Shopify.';
        } else {
          deliverySummary.textContent = 'Selecciona tu distrito para aplicar S/30, S/40 o S/50. Envío gratis desde S/1,500 únicamente en los distritos tarifados.';
        }
      }

      function updateReceiptState() {
        const isBoleta = receipt?.value === 'Boleta';
        const isFactura = receipt?.value === 'Factura';

        setGroup(boletaFields, isBoleta);
        setGroup(facturaFields, isFactura);
        setRequired(dni, isBoleta);
        setRequired(ruc, isFactura);
        setRequired(businessName, isFactura);
        setRequired(fiscalAddress, isFactura);

        if (!isBoleta) clearField(dni);
        if (!isFactura) {
          clearField(ruc);
          clearField(businessName);
          clearField(fiscalAddress);
        }
      }

      function collectAttributes() {
        const attributes = { ...storedAttributes };

        form?.querySelectorAll('[name^="attributes\\["]').forEach((field) => {
          const match = field.name.match(/^attributes\[(.+)\]$/);
          if (!match) return;
          attributes[match[1]] = field.value;
        });

        const deliveryKind = selectedDeliveryKind();
        if (deliveryKind !== 'local') {
          attributes['Fecha de entrega preferida'] = '';
          attributes['Turno de entrega'] = '';
        }
        if (deliveryKind !== 'province') attributes['Agencia de transporte'] = '';

        if (receipt?.value !== 'Boleta') attributes['DNI para boleta'] = '';
        if (receipt?.value !== 'Factura') {
          attributes['RUC para factura'] = '';
          attributes['Razón social'] = '';
          attributes['Dirección fiscal'] = '';
        }

        return attributes;
      }

      function persistAttributes(strict = false) {
        if (!form || typeof window.fetch !== 'function') {
          if (strict) return Promise.reject(new Error('No se pudieron guardar los datos del pedido.'));
          return Promise.resolve();
        }

        const attributes = collectAttributes();
        const root = window.Shopify?.routes?.root || '/';

        const request = saveChain
          .catch(() => {})
          .then(async () => {
            const response = await window.fetch(`${root}cart/update.js`, {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ attributes }),
            });

            if (!response.ok) throw new Error('No se pudieron guardar los datos del pedido.');
            storedAttributes = attributes;
          });

        saveChain = strict
          ? request
          : request.catch(() => {
              // A later input or checkout attempt retries the save.
            });

        return saveChain;
      }

      async function requestCartJson(url, options) {
        const response = await window.fetch(url, options);
        if (!response.ok) throw new Error('No se pudo actualizar el pedido.');
        return typeof response.json === 'function' ? response.json() : null;
      }

      function cartItemVariantId(item) {
        return Number(item?.variant_id || item?.variantId || item?.id) || 0;
      }

      function cartItemHandle(item) {
        return String(item?.handle || item?.product_handle || '');
      }

      function isShippingCartItem(item) {
        return shippingVariantIds.has(cartItemVariantId(item))
          || cartItemHandle(item) === shippingHandle
          || String(item?.properties?._bless_home_shipping) === 'true';
      }

      function isInstallationCartItem(item) {
        return cartItemHandle(item) === installationHandle;
      }

      function cartLinePrice(item) {
        return Number(item?.final_line_price ?? item?.line_price ?? 0) || 0;
      }

      function formatCartMoney(cents) {
        const amount = new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(Math.max(0, Number(cents) || 0) / 100);
        const currencySuffix = /\bPEN\b/.test(section.dataset.bhCartTotalFormatted || '') ? ' PEN' : '';
        return `S/ ${amount}${currencySuffix}`;
      }

      function analyzeCart(cart) {
        const items = Array.isArray(cart?.items) ? cart.items : [];
        const currentShippingLines = items.filter(isShippingCartItem);
        const currentInstallationLines = items.filter(isInstallationCartItem);
        const merchandiseLines = items.filter(
          (item) => !isShippingCartItem(item) && !isInstallationCartItem(item)
        );
        const shippingTotal = currentShippingLines.reduce(
          (total, item) => total + cartLinePrice(item),
          0
        );
        const eligibleTotal = merchandiseLines.reduce(
          (total, item) => total + cartLinePrice(item),
          0
        );
        const checkoutSubtotal = Math.max(0, (Number(cart?.total_price) || 0) - shippingTotal);

        return {
          cart,
          items,
          shippingLines: currentShippingLines,
          installationLines: currentInstallationLines,
          hasMerchandise: merchandiseLines.length > 0,
          isFreeShipping: eligibleTotal >= freeShippingThreshold,
          eligibleTotal,
          checkoutSubtotal,
          freeShippingRemaining: Math.max(0, freeShippingThreshold - eligibleTotal),
        };
      }

      function applyCartState(state) {
        if (!state || !section.isConnected) return;

        isFreeShipping = state.isFreeShipping;
        hasMerchandise = state.hasMerchandise;
        shippingLines = state.shippingLines;
        originalCartTotalValue = formatCartMoney(state.checkoutSubtotal);

        section.dataset.bhFreeShipping = String(isFreeShipping);
        section.dataset.bhHasMerchandise = String(hasMerchandise);
        section.dataset.bhCartTotalFormatted = originalCartTotalValue;
        section.dataset.bhFreeShippingRemainingFormatted = formatCartMoney(
          state.freeShippingRemaining
        );

        ['30', '40', '50'].forEach((zonePrice) => {
          const shippingPrice = Number(zonePrice) * 100;
          section.dataset[`bhZone${zonePrice}ShippingFormatted`] = formatCartMoney(shippingPrice);
          section.dataset[`bhZone${zonePrice}TotalFormatted`] = formatCartMoney(
            state.checkoutSubtotal + shippingPrice
          );
        });

        updateDeliveryState();
        if (!hasMerchandise) {
          setCheckoutBlocked(true);
          setShippingStatus('Añade al menos un mueble antes de continuar al pago.', true);
        } else if (!cartMutationPending && !checkoutInProgress) {
          setCheckoutBlocked(false);
        }
      }

      async function fetchCurrentCartState() {
        if (typeof window.fetch !== 'function') {
          throw new Error('No se pudo conectar con el carrito. Inténtalo nuevamente.');
        }

        const requestVersion = ++cartStateVersion;
        const root = window.Shopify?.routes?.root || '/';
        const cart = await requestCartJson(`${root}cart.js`, {
          method: 'GET',
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        });
        const state = analyzeCart(cart);
        if (requestVersion === cartStateVersion) applyCartState(state);
        return state;
      }

      async function reconcileInstallationCharge() {
        const initialState = await fetchCurrentCartState();
        if (initialState.installationLines.length === 0) return initialState;

        const root = window.Shopify?.routes?.root || '/';
        let keptInstallation = false;

        for (const line of initialState.installationLines) {
          const shouldKeep = !keptInstallation;
          keptInstallation = true;

          if (shouldKeep && Number(line.quantity) === 1) continue;

          await requestCartJson(`${root}cart/change.js`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: line.key, quantity: shouldKeep ? 1 : 0 }),
          });
        }

        const verifiedState = await fetchCurrentCartState();
        const hasSingleInstallation = verifiedState.installationLines.length === 1
          && Number(verifiedState.installationLines[0].quantity) === 1;

        if (!hasSingleInstallation) {
          throw new Error('No se pudo verificar la instalación de mesa. Inténtalo nuevamente.');
        }

        return verifiedState;
      }

      async function reconcileShippingCharge() {
        if (typeof window.fetch !== 'function') {
          throw new Error('No se pudo conectar con el carrito. Inténtalo nuevamente.');
        }

        const root = window.Shopify?.routes?.root || '/';
        const initialState = await fetchCurrentCartState();
        const kind = selectedDeliveryKind();
        const desiredVariantId = selectedShippingVariantId();

        if (
          initialState.hasMerchandise
          && kind === 'local'
          && !initialState.isFreeShipping
          && (!shippingConfigured || !desiredVariantId)
        ) {
          throw new Error('La tarifa seleccionada no está disponible. Actualiza la página e inténtalo nuevamente.');
        }

        let keptDesiredLine = false;

        for (const line of initialState.shippingLines) {
          const variantId = cartItemVariantId(line);
          const shouldKeep = desiredVariantId && variantId === desiredVariantId && !keptDesiredLine;

          if (shouldKeep) {
            keptDesiredLine = true;
            if (Number(line.quantity) === 1) continue;
          }

          await requestCartJson(`${root}cart/change.js`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: line.key, quantity: shouldKeep ? 1 : 0 }),
          });
        }

        if (desiredVariantId && !keptDesiredLine) {
          const option = destination?.selectedOptions[0];
          await requestCartJson(`${root}cart/add.js`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: [{
                id: desiredVariantId,
                quantity: 1,
                properties: {
                  _bless_home_shipping: 'true',
                  _bless_home_shipping_zone: option?.dataset.zoneLabel || '',
                },
              }],
            }),
          });
        }

        const verifiedState = await fetchCurrentCartState();
        const expectedVariantId = selectedShippingVariantId();
        const shouldHaveShipping = (
          verifiedState.hasMerchandise
          && selectedDeliveryKind() === 'local'
          && !verifiedState.isFreeShipping
        );
        const hasExactShipping = (
          verifiedState.shippingLines.length === 1
          && cartItemVariantId(verifiedState.shippingLines[0]) === expectedVariantId
          && Number(verifiedState.shippingLines[0].quantity) === 1
        );

        if (
          (shouldHaveShipping && !hasExactShipping)
          || (!shouldHaveShipping && verifiedState.shippingLines.length > 0)
        ) {
          throw new Error('No se pudo verificar la tarifa final. Inténtalo nuevamente.');
        }

        return verifiedState;
      }

      function shippingLineSignature(state) {
        return state.shippingLines
          .map((line) => `${cartItemVariantId(line)}:${Number(line.quantity) || 0}`)
          .sort()
          .join('|');
      }

      async function synchronizeShippingCharge(reloadWhenChanged = false) {
        const beforeState = await fetchCurrentCartState();
        const beforeSignature = shippingLineSignature(beforeState);
        const verifiedState = await reconcileShippingCharge();
        const shippingChanged = beforeSignature !== shippingLineSignature(verifiedState);

        if (shippingChanged && reloadWhenChanged) {
          window.location.reload();
          return { state: verifiedState, reloading: true };
        }

        return { state: verifiedState, reloading: false };
      }

      function shippingSyncMessage() {
        const kind = selectedDeliveryKind();
        if (kind === 'province') return 'Envío por agencia seleccionado. Flete por pagar en destino.';
        if (kind === 'local' && isFreeShipping) return 'Envío gratuito aplicado.';
        if (kind === 'local') return 'Tarifa de envío actualizada.';
        return '';
      }

      function navigateToCheckout() {
        const root = window.Shopify?.routes?.root || '/';
        const checkoutUrl = `${root}checkout`;

        if (typeof window.__bhNavigateToCheckout === 'function') {
          window.__bhNavigateToCheckout(checkoutUrl);
        } else {
          window.location.assign(checkoutUrl);
        }
      }

      async function handleCheckoutSubmit(event) {
        const submitter = event.submitter;
        const isCheckoutSubmission = submitter?.name === 'checkout'
          || checkoutButtons.includes(submitter);

        if (!isCheckoutSubmission) return;
        event.preventDefault();
        if (checkoutInProgress) return;

        if (!hasMerchandise) {
          setCheckoutBlocked(true);
          setShippingStatus('Añade al menos un mueble antes de continuar al pago.', true);
          return;
        }

        if (cartMutationPending) {
          setShippingStatus('Espera un momento mientras recalculamos el carrito.');
          return;
        }

        if (!form?.reportValidity()) {
          form?.querySelector(':invalid')?.focus({ preventScroll: false });
          return;
        }

        checkoutInProgress = true;
        section.setAttribute('aria-busy', 'true');
        setCheckoutBlocked(true);
        setShippingStatus('Aplicando la tarifa seleccionada al pedido…');

        try {
          window.clearTimeout(saveTimer);
          await fetchCurrentCartState();
          await reconcileInstallationCharge();
          await reconcileShippingCharge();
          await persistAttributes(true);
          setShippingStatus(isFreeShipping ? 'Envío gratuito aplicado.' : 'Tarifa aplicada. Abriendo el pago…');
          navigateToCheckout();
        } catch (error) {
          checkoutInProgress = false;
          section.removeAttribute('aria-busy');
          setCheckoutBlocked(false);
          setShippingStatus(error?.message || 'No pudimos aplicar el envío. Inténtalo nuevamente.', true);
          destination?.reportValidity();
        }
      }

      function handleCartFormKeydown(event) {
        if (event.key !== 'Enter' || event.isComposing) return;
        if (!(event.target instanceof HTMLInputElement)) return;
        if (!event.target.matches('.quantity-selector input, [data-cart-quantity]')) return;

        // Quantity controls own their update cycle. Prevent the browser from
        // interpreting Enter as a click on the checkout submit button.
        event.preventDefault();
        event.stopPropagation();
        event.target.blur();
      }

      function handleQuantityUpdateIntent(event) {
        if (!(event.target instanceof Node) || !checkoutScope.contains(event.target)) return;

        cartMutationPending = true;
        section.setAttribute('aria-busy', 'true');
        setCheckoutBlocked(true);
        setShippingStatus('Recalculando productos y envío…');
      }

      function handleCartMutationEvent(event) {
        const mutationVersion = ++cartMutationVersion;
        cartMutationPending = true;
        section.setAttribute('aria-busy', 'true');
        setCheckoutBlocked(true);
        setShippingStatus('Recalculando productos y envío…');
        window.clearTimeout(cartStateRefreshTimer);

        const mutationPromise = event?.promise && typeof event.promise.then === 'function'
          ? event.promise
          : Promise.resolve();

        mutationPromise
          .then(() => {
            if (mutationVersion !== cartMutationVersion) return;

            cartStateRefreshTimer = window.setTimeout(async () => {
              try {
                if (selectedDeliveryKind()) {
                  const syncResult = await synchronizeShippingCharge(true);
                  if (syncResult.reloading) return;
                } else {
                  await fetchCurrentCartState();
                }
                if (mutationVersion !== cartMutationVersion) return;
                cartMutationPending = false;
                section.removeAttribute('aria-busy');
                if (!checkoutInProgress) setCheckoutBlocked(false);
                setShippingStatus(shippingSyncMessage());
              } catch (_error) {
                if (mutationVersion !== cartMutationVersion) return;
                cartMutationPending = false;
                section.removeAttribute('aria-busy');
                if (!checkoutInProgress) setCheckoutBlocked(false);
                setShippingStatus('No pudimos recalcular el carrito. Inténtalo nuevamente.', true);
              }
            }, 75);
          })
          .catch(() => {
            if (mutationVersion !== cartMutationVersion) return;
            cartMutationPending = false;
            section.removeAttribute('aria-busy');
            if (!checkoutInProgress) setCheckoutBlocked(false);
            setShippingStatus('No pudimos actualizar el carrito. Inténtalo nuevamente.', true);
          });
      }

      function scheduleAttributeSave() {
        window.clearTimeout(saveTimer);
        saveTimer = window.setTimeout(persistAttributes, 500);
      }

      async function updateInstallation(event) {
        const checkbox = event.currentTarget;
        const variantId = Number(checkbox.dataset.variantId);
        const lineKey = checkbox.dataset.lineKey;
        const root = window.Shopify?.routes?.root || '/';

        if (!variantId || typeof window.fetch !== 'function') return;

        if (installationStatus) installationStatus.textContent = '';
        checkbox.disabled = true;
        section.setAttribute('aria-busy', 'true');
        setCheckoutBlocked(true);

        try {
          const endpoint = checkbox.checked ? `${root}cart/add.js` : `${root}cart/change.js`;
          const body = checkbox.checked
            ? { items: [{ id: variantId, quantity: 1 }] }
            : { id: lineKey, quantity: 0 };
          const response = await window.fetch(endpoint, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });

          if (!response.ok) throw new Error('No se pudo actualizar la instalación.');
          window.location.reload();
        } catch (_error) {
          checkbox.checked = !checkbox.checked;
          checkbox.disabled = false;
          section.removeAttribute('aria-busy');
          if (installationStatus) {
            installationStatus.textContent = 'No pudimos actualizar la instalación. Inténtalo nuevamente.';
          }
          setCheckoutBlocked(false);
          updateDeliveryState();
        }
      }

      async function updateDestination() {
        updateDeliveryState();
        window.clearTimeout(saveTimer);
        section.setAttribute('aria-busy', 'true');
        setCheckoutBlocked(true);
        setShippingStatus('Actualizando la tarifa del pedido…');

        try {
          await persistAttributes(true);
          const syncResult = await synchronizeShippingCharge(true);
          if (syncResult.reloading) return;

          section.removeAttribute('aria-busy');
          setCheckoutBlocked(false);
          setShippingStatus(shippingSyncMessage());
        } catch (error) {
          section.removeAttribute('aria-busy');
          setCheckoutBlocked(false);
          setShippingStatus(error?.message || 'No pudimos actualizar la tarifa. Inténtalo nuevamente.', true);
        }
      }

      destination?.addEventListener('change', updateDestination, { signal });
      receipt?.addEventListener('change', () => {
        updateReceiptState();
        scheduleAttributeSave();
      }, { signal });
      deliveryDate?.addEventListener('change', () => {
        updateDateRules();
        scheduleAttributeSave();
      }, { signal });
      deliveryWindow?.addEventListener('change', scheduleAttributeSave, { signal });
      installation?.addEventListener('change', updateInstallation, { signal });
      form?.addEventListener('keydown', handleCartFormKeydown, { signal });
      form?.addEventListener('submit', handleCheckoutSubmit, { signal });
      checkoutScope.addEventListener('quantity-selector:update', handleQuantityUpdateIntent, { signal });
      document.addEventListener('shopify:cart:lines-update', handleCartMutationEvent, { signal });
      document.addEventListener('shopify:cart:discount-update', handleCartMutationEvent, { signal });

      section.querySelectorAll('input[name^="attributes\\["]').forEach((input) => {
        input.addEventListener('input', scheduleAttributeSave, { signal });
        input.addEventListener('change', scheduleAttributeSave, { signal });
      });

      updateDeliveryState();
      updateReceiptState();
      fetchCurrentCartState().catch(() => {
        // The server-rendered state remains as a safe fallback.
      });
    });
  }

  let checkoutObserver;

  function observeCheckoutDetails() {
    if (checkoutObserver || !document.body || !('MutationObserver' in window)) return;

    const selector = '[data-bh-checkout-details]';
    checkoutObserver = new MutationObserver((records) => {
      const affectedSections = new Set();

      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;

          if (node.matches(selector)) affectedSections.add(node);
          node.querySelectorAll(selector).forEach((section) => affectedSections.add(section));

          const parentSection = node.parentElement?.closest(selector);
          if (parentSection) affectedSections.add(parentSection);
        });
      });

      affectedSections.forEach((section) => initCheckoutDetails(section, true));
    });

    checkoutObserver.observe(document.body, { childList: true, subtree: true });
  }

  function initProductGalleries(scope) {
    const selector = 'main[data-template^="product"] media-gallery slideshow-component';
    const slideshows = [...scope.querySelectorAll(selector)];

    if (scope instanceof Element && scope.matches(selector)) slideshows.unshift(scope);

    slideshows.forEach((slideshow) => {
      if (slideshow.dataset.bhGalleryReady === 'true') return;
      if (!slideshow.querySelector('slideshow-arrows')) return;

      slideshow.dataset.bhGalleryReady = 'true';
      slideshow.removeAttribute('infinite');

      const initialIndex = Number(slideshow.getAttribute('initial-slide') || 0);
      const initialSlides = slideshow.querySelectorAll(
        'slideshow-slides > slideshow-slide:not([hidden])'
      );
      const previous = slideshow.querySelector('.slideshow-control--previous');
      const next = slideshow.querySelector('.slideshow-control--next');

      if (previous) previous.disabled = initialIndex <= 0;
      if (next) next.disabled = initialIndex >= initialSlides.length - 1;

      slideshow.addEventListener(
        'click',
        (event) => {
          const control = event.target.closest(
            '.slideshow-control--previous, .slideshow-control--next'
          );
          if (!control || !slideshow.contains(control)) return;

          window.clearTimeout(slideshow.bhGalleryTimer);
          slideshow.bhGalleryTimer = window.setTimeout(() => {
            const scroller = slideshow.querySelector('slideshow-slides');
            const slides =
              slideshow.slides ||
              (scroller
                ? [...scroller.querySelectorAll(':scope > slideshow-slide:not([hidden])')]
                : []);
            const target = slides[Number(slideshow.current)];

            if (!scroller || !target) return;
            if (typeof scroller.scrollTo === 'function') {
              scroller.scrollTo({ left: target.offsetLeft, behavior: 'instant' });
            } else {
              scroller.scrollLeft = target.offsetLeft;
            }
          }, 100);
        },
        true
      );
    });
  }

  function initPurchaseFirstExperience(scope) {
    scope.querySelectorAll('[aria-hidden="true"]').forEach((element) => {
      const text = element.textContent || '';
      if (/[\u2192\u2197]/.test(text) && /^[\s\u2192\u2197]+$/.test(text)) element.remove();
    });

    scope.querySelectorAll('.bh-home a[href*="wa.me"]').forEach((link) => link.remove());

    scope.querySelectorAll('#header-group a[href*="wa.me"]').forEach((link) => {
      if (!link.textContent.includes('Envíos')) return;
      link.textContent = 'Envío gratis desde S/1,500 en distritos tarifados';
      link.removeAttribute('href');
      link.removeAttribute('target');
    });

    const legacyBenefits = scope.querySelectorAll('.bh-home .bh-benefit');
    if (legacyBenefits.length >= 4) {
      const purchaseHeading = legacyBenefits[1].querySelector('h3');
      const purchaseCopy = legacyBenefits[1].querySelector('p');
      const aftercareHeading = legacyBenefits[3].querySelector('h3');
      const aftercareCopy = legacyBenefits[3].querySelector('p');

      if (purchaseHeading) purchaseHeading.textContent = 'Compra directa';
      if (purchaseCopy) purchaseCopy.textContent = 'Elige tu pieza, añádela al carrito y continúa al pago.';
      if (aftercareHeading) aftercareHeading.textContent = 'Atención postventa';
      if (aftercareCopy) aftercareCopy.textContent = 'Te acompañamos después de tu compra cuando lo necesites.';
    }

    scope.querySelector('.bh-footer__cta')?.remove();
    scope.querySelectorAll('.bh-footer__nav p').forEach((paragraph) => {
      if (paragraph.textContent.includes('WhatsApp')) paragraph.textContent = 'Lima, Perú';
    });
    scope.querySelectorAll('.bh-footer__payment span').forEach((item) => {
      if (item.textContent.includes('Compra por web')) item.textContent = 'Compra directa en la web';
    });
  }

  let exclusiveOverlayReady = false;

  function initExclusivePurchaseOverlays() {
    if (exclusiveOverlayReady) return;
    exclusiveOverlayReady = true;

    const searchModal = document.getElementById('search-modal');

    searchModal?.addEventListener('dialog:open', () => {
      const cartDrawer = document.getElementById('cart-drawer');
      if (cartDrawer?.hasAttribute('open') && typeof cartDrawer.close === 'function') {
        cartDrawer.close();
      }
    });

    document.addEventListener('theme-drawer:open', (event) => {
      if (!(event.target instanceof Element) || event.target.id !== 'cart-drawer') return;
      if (typeof searchModal?.closeDialog === 'function') searchModal.closeDialog();
    });
  }

  function init(scope = document) {
    initReveals(scope);
    initMotion(scope);
    initSkipToContent(scope);
    initCategoryNavigation(scope);
    initContactForms(scope);
    initProductDeliveryEstimates(scope);
    initProductVariantState();
    initCheckoutDetails(scope);
    initProductGalleries(scope);
    initPurchaseFirstExperience(scope);
    initExclusivePurchaseOverlays();
  }

  function start() {
    init();
    observeCheckoutDetails();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
